import { SelectionModel } from '@angular/cdk/collections';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ViewChild,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  AfterViewInit,
  ChangeDetectorRef,
  signal
} from '@angular/core';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { combineLatest, of, ReplaySubject, Subject, Observable, BehaviorSubject } from 'rxjs';
import {catchError, filter, map, switchMap, startWith, debounceTime, first, tap, withLatestFrom} from 'rxjs/operators';
import {
  AffiliateAdRequest,
  AffiliateExclusiveOption,
  EntityGroupName,
  MerchantAd,
  MerchantAdRequest,
  Paged, TagExclusiveOption
} from '../../../core/dtos/api';
import { traverse } from '../../../core/pure-utils/object-utils';
import { AffiliateApiService } from '../../../core/services/api/affiliate-api.service';
import {DashboardApiService} from '../../../core/services/api/dashboard-api.service';
import { MerchantApiService } from '../../../core/services/api/merchant-api.service';
import {MasterToggleState, FilterMap, MerchantCommon} from '../ad-management';
import { AdManagementService } from '../ad-management.service';
import { EditAdsDialogComponent } from './edit-ads-dialog/edit-ads-dialog.component';
import {PreviewAdDialogComponent} from './preview-ad-dialog/preview-ad-dialog.component';
import { ScheduleAdsDialogComponent } from './schedule-ads-dialog/schedule-ads-dialog.component';
import {ActiveAffiliatesDialogComponent} from './active-affiliates-dialog/active-affiliates-dialog.component';
import {ConversionRateDialogComponent} from './conversion-rate-dialog/conversion-rate-dialog.component';
import { GetLinkDialogComponent } from '../affiliate-ads/get-link-dialog/get-link-dialog.component';
import {triggerHotJarGa} from '../../../core/pure-utils/analytics';
import {TagApiService} from '../../../core/services/api/tag-api.service';
import {toObservable} from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-merchant-ads',
    templateUrl: './merchant-ads.component.html',
    styleUrls: ['./merchant-ads.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MerchantAdsComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(private merchantApiService: MerchantApiService, private dashboardApiService: DashboardApiService,
    private adManagementService: AdManagementService, private dialog: MatDialog, private affiliateApiService: AffiliateApiService,
    private changeDetectorRef: ChangeDetectorRef, private tagApiService: TagApiService) { }
  public triggerAnalytics = triggerHotJarGa;
  _filterMap$ = new ReplaySubject<FilterMap>(1);
  filterMap$ = this._filterMap$.pipe(filter(x => x != null));
  selection = new SelectionModel<MerchantAd>(true, []);
  masterToggleState$ = new ReplaySubject<boolean>(1);
  @Input() set filterMap(value: FilterMap) { this._filterMap$.next(value); }
  // This input is an object rather than a boolean as we want the value to be emitted each time even if it is the same value.
  @Input() set masterToggleChecked(toggleValue: {checked: boolean}) {
    this.masterToggleState$.next(toggleValue.checked);
  }
  @Output() selectionChange = new EventEmitter<MerchantAd[]>();
  @Output() requestParamsChange = new EventEmitter<AffiliateAdRequest | MerchantAdRequest>();
  @Output() masterToggleState = new EventEmitter<MasterToggleState>();

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  ngAfterViewInit$ = new ReplaySubject<void>(1);
  refreshDataSource$ = new BehaviorSubject<void>(null);
  isLoadingResults = true;
  dataError = false;
  totalItems = 0;
  dataSource = signal([] as MerchantAd[]);
  visibleColumns: string[] = [];
  private userAffiliates: AffiliateExclusiveOption[];
  private userTagGroups: TagExclusiveOption[];
  userAffiliates$Sub = this.dashboardApiService.usersAffiliates$.subscribe(affiliates => this.userAffiliates = affiliates);
  userTagGroups$Sub = this.tagApiService.usersTagGroups$.subscribe(tags => this.userTagGroups = tags);

  currentAccountFiltered$ = this.dashboardApiService.currentAccount$.pipe(filter(x => ['merchants', 'affiliates'].includes(x.entity_group_name)));
  currentAccountFiltered1st$Sub = this.currentAccountFiltered$.pipe(first()).subscribe(x => this.visibleColumns = this.visibleColumnsByGroup(x.entity_group_name));
  currentGroupMerchant$ = this.currentAccountFiltered$.pipe(map(x => x.entity_group_name === 'merchants'));

  selectionChanged$Sub = this.selection.changed
    .subscribe(x => {
      this.selectionChange.emit(x.source.selected);
      let masterToggleState: MasterToggleState = this.isAllSelected() ? 'checked' : this.selection.hasValue() ? 'indeterminate' : 'unchecked';
      this.masterToggleState.emit(masterToggleState);
    });
  updateSelectionFromParent$Sub = combineLatest([this.masterToggleState$, toObservable(this.dataSource)])
    .subscribe(([toggleState, dataSource]) => {
      if (toggleState === true) {
        this.selection.select(...dataSource);
      } else {
        this.selection.clear();
      }
    });

  dataSource$Sub = this.ngAfterViewInit$.pipe(
    switchMap(() => combineLatest([
        this.filterMap$.pipe(startWith(<FilterMap>{}), tap(() => this.paginator.pageIndex = 0)),
        this.sort.sortChange.pipe(debounceTime(250), startWith({}), tap(() => this.paginator.pageIndex = 0)),
        this.paginator.page.pipe(debounceTime(250), startWith({})),
        this.refreshDataSource$
      ])
    ),
    withLatestFrom(this.currentAccountFiltered$),
    switchMap(([[filterMap], currentAccount]) => {
      this.isLoadingResults = true;

      let sortField = this.sort.active === 'ad' ? 'ad_type' :
        this.sort.active === 'adDetails' ? 'ad_title' :
        this.sort.active === 'date' ? 'sort_updated_at' :
        this.sort.active === 'promotionDetails' ? 'promotion_details' :
        this.sort.active === 'performance' ?
          currentAccount.entity_group_name === 'merchants' ? 'calculated_active_websites' : 'calculated_conversion_rate'
          : this.sort.active;

      let requestQuery: AffiliateAdRequest | MerchantAdRequest = {
        sort: sortField,
        direction: this.sort.direction,
        limit: this.paginator.pageSize,
        page: this.paginator.pageIndex + 1,

        ad_title: filterMap.adTitle === '' ? null : filterMap.adTitle,
        ad_type: filterMap.adType === 'all' ? null : filterMap.adType,
        dynamic_label: filterMap.dynamicLabel === 'all' ? null : filterMap.dynamicLabel,
        image_height: filterMap.imageHeight,
        image_width: filterMap.imageWidth,
        merchant_ad_id: filterMap.classicMerchantAdId === '' ? null : filterMap.classicMerchantAdId,
        // filterMap.merchant is never a string here, strings filtered before used as an input
        merchant_id: filterMap.merchant === this.adManagementService.allMerchants ? null :
          (<MerchantCommon> filterMap.merchant)?.merchant_id,
        status: filterMap.status === 'all' ? null : filterMap.status
      };

      // delete all null properties from request query;
      traverse(requestQuery, (currentObject, propName, value) => {
        if (value === null) {
          delete currentObject[propName];
        }
      });

      let adRequest$: Observable<Paged<MerchantAd>>;
      if (currentAccount.entity_group_name === 'merchants') {
        delete requestQuery.merchant_id;
        adRequest$ = this.merchantApiService.getSinglePageOfMerchantAds(currentAccount.entity_id, <MerchantAdRequest> requestQuery);
      } else {
        delete requestQuery.status;
        adRequest$ = this.affiliateApiService.getAffiliateMerchantAds(currentAccount.entity_id, <AffiliateAdRequest> requestQuery);
      }
      this.requestParamsChange.emit(requestQuery);

      return adRequest$.pipe(
        map(data => {
          this.isLoadingResults = false;
          this.dataError = false;
          this.selection.clear();
          this.totalItems = data.total_items;
          this.dataSource.set(data.items);
          this.visibleColumns = this.visibleColumnsByGroup(currentAccount.entity_group_name);
          this.changeDetectorRef.detectChanges();
          return data.items;
        }),
        catchError(() => {
          this.isLoadingResults = false;
          this.dataError = true;
          this.selection.clear();
          this.totalItems = 0;
          this.dataSource.set([] as MerchantAd[]);
          this.visibleColumns = this.visibleColumnsByGroup(currentAccount.entity_group_name);
          this.changeDetectorRef.detectChanges();
          return of<MerchantAd[]>([]);
        })
      );
    })
  ).subscribe();

  visibleColumnsByGroup(entityGroupName: EntityGroupName) {
    if (entityGroupName === 'merchants') {
      return ['actions', 'ad', 'adDetails', 'performance', 'date'];
    } else if (entityGroupName === 'affiliates') {
      return ['actionsAffiliate', 'ad', 'adDetails', 'performance', 'date'];
    } else {
      return [];
    }
  }

  isAllSelected() {
    let numSelected = this.selection.selected.length;
    let numRows = this.dataSource().length;
    return numSelected === numRows;
  }

  openEditAdsDialog(merchantAd: MerchantAd) {
    let dialogRef = this.dialog.open(EditAdsDialogComponent, {
      data: {merchantAds: [merchantAd], userAffiliates: this.userAffiliates, userTagGroups: this.userTagGroups},
      disableClose: true,
      width: '100%',
      maxWidth: '90vw'
    });

    dialogRef.afterClosed().subscribe((saved: boolean) => {
      if (saved) {
        this.refreshDataSource$.next();
      }
    });
  }

  openScheduleAdsDialog(merchantAd: MerchantAd) {
    let dialogRef = this.dialog.open(ScheduleAdsDialogComponent, {
      data: merchantAd,
      disableClose: true,
      width: '80%',
      maxWidth: '90vw'
    });

    dialogRef.afterClosed().subscribe((saved: boolean) => {
      if (saved) {
        this.refreshDataSource$.next();
      }
    });
  }

  onGetLinkClick(merchantAd: MerchantAd) {
    this.dialog.open(GetLinkDialogComponent, {
      data: merchantAd,
      width: '550px',
    });
  }

  openAdPreview(merchantAd: MerchantAd) {
    let dialogRef: MatDialogRef<PreviewAdDialogComponent, 'edit'|'getLink'> = this.dialog.open(PreviewAdDialogComponent, {
      data: merchantAd,
      maxHeight: '90vh',
      minWidth: '300px'
    });

    dialogRef.afterClosed().subscribe(modalAction => {
      if (modalAction) {
        if (modalAction === 'edit') {
          this.openEditAdsDialog(merchantAd);
        }
        if (modalAction === 'getLink') {
          this.onGetLinkClick(merchantAd);
        }
      }
    });
  }

  openActiveAffiliates(merchantAd: MerchantAd) {
    let dialogRef: MatDialogRef<ActiveAffiliatesDialogComponent> = this.dialog.open(ActiveAffiliatesDialogComponent, {
      data: merchantAd
    });
  }

  openConversionRate(merchantAd: MerchantAd) {
    let dialogRef: MatDialogRef<ConversionRateDialogComponent> = this.dialog.open(ConversionRateDialogComponent, {
      data: merchantAd
    });
  }

  ngOnInit() { }

  ngAfterViewInit() {
    this.ngAfterViewInit$.next();
    this.ngAfterViewInit$.complete();
  }

  ngOnDestroy() {
    this.updateSelectionFromParent$Sub.unsubscribe();
    this.dataSource$Sub.unsubscribe();
    this.selectionChanged$Sub.unsubscribe();
    this.userAffiliates$Sub.unsubscribe();
    this.userTagGroups$Sub.unsubscribe();
  }
}
